import {
  DeleteOutlined,
  Edit,
  MoreVert,
  SettingsOutlined,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { EmailEditorDrawerDataType } from 'components/drawers/email-templates-drawers/email-editor-drawer/email-editor-drawer.component';
import { useGlobalDrawer } from 'components/drawers/global-drawer/global-drawer.hooks';
import { useIncPageTabPanelsContext } from 'components/inc-page/inc-page-tab-panels/inc-page-tab-panels.context';
import { useHasPermission } from 'hooks/authorization.hooks';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';

export type MenuPopoverProps = {
  openEmailEditorDrawer: (drawerData: EmailEditorDrawerDataType) => void;
} & EmailEditorDrawerDataType['template'];

const MenuPopover = ({
  templateId,
  jsonTemplate,
  name,
  emailTemplateType,
  reusable,
  openEmailEditorDrawer,
  ...rest
}: MenuPopoverProps) => {
  const { t } = useTranslation();

  const { tabPath } = useIncPageTabPanelsContext();

  const menuPopupState = usePopupState({
    variant: 'popover',
    popupId: 'location-popup-menu',
  });

  const { open: openDeleteEmailTemplateDrawer } = useGlobalDrawer(
    'globalDeleteEmailTemplateDrawer',
  );
  const { open: openEmailTemplateSettingsDrawer } = useGlobalDrawer(
    'globalEditEmailTemplateSettingsDrawer',
  );

  const hasDeletePermission = useHasPermission('DELETE_EMAIL_TEMPLATES');
  const hasEditPermission = useHasPermission('CREATE_EDIT_EMAIL_TEMPLATES');

  if (!hasDeletePermission && !hasEditPermission) return null;

  return (
    <Box>
      <IconButton
        {...bindTrigger(menuPopupState)}
        data-testid="EmailTemplateSettings.IconButton"
        aria-label={t(
          'page.communications.emailTemplates.searchDataCardGrid.card.menuPopover.actions.aria-label',
        )}
      >
        <MoreVert />
      </IconButton>
      <Menu {...bindMenu(menuPopupState)}>
        {tabPath !== 'event' && hasEditPermission && (
          <MenuItem
            onClick={() => {
              openEmailTemplateSettingsDrawer({ template: { templateId } });
              menuPopupState.close();
            }}
            data-testid="EmailTemplateSettings.EditEmailTemplateSettings.Option"
          >
            <ListItemIcon>
              <SettingsOutlined />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'page.communications.emailTemplates.searchDataCardGrid.card.menuPopover.actions.editEmailSettings.label',
              )}
            />
          </MenuItem>
        )}
        {hasEditPermission && (
          <Box>
            <MenuItem
              onClick={() => {
                openEmailEditorDrawer({
                  template: {
                    templateId,
                    jsonTemplate,
                    name,
                    emailTemplateType,
                    reusable,
                    ...rest,
                  },
                });
                menuPopupState.close();
              }}
              data-testid="EmailTemplateSettings.EditEmailTemplate.Option"
            >
              <ListItemIcon>
                <Edit />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  'page.communications.emailTemplates.searchDataCardGrid.card.menuPopover.actions.editEmailTemplate.label',
                )}
              />
            </MenuItem>
          </Box>
        )}

        {tabPath !== 'event' && hasDeletePermission && (
          <MenuItem
            onClick={() => {
              openDeleteEmailTemplateDrawer({
                templateId,
              });
              menuPopupState.close();
            }}
            data-testid="EmailTemplateSettings.DeleteEmailTemplate.Option"
          >
            <ListItemIcon>
              <DeleteOutlined color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t(
                'page.communications.emailTemplates.searchDataCardGrid.card.menuPopover.actions.deleteTemplate.label',
              )}
            />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default MenuPopover;
